'use client';

import React, { useState, useEffect } from 'react';
import { Modal } from '@/app/components/ui/Modal';
import { Button } from '@/app/components/ui/Button';
import { createClient } from '@/lib/supabase/client';
import { requestMagicLink } from '@/lib/auth/magic-link';
import { useWillStore } from '@/lib/store';
import { Loader2, Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';

interface SignInModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    title?: string;
    initialEmail?: string;
    autoSendOtp?: boolean;
}

export const SignInModal = ({
    isOpen,
    onClose,
    onSuccess,
    title = "Sign In",
    initialEmail = '',
    autoSendOtp = false
}: SignInModalProps) => {
    const { setAuth } = useWillStore();
    const [email, setEmail] = useState(initialEmail);
    const [isSending, setIsSending] = useState(false);
    const [step, setStep] = useState<'email' | 'otp'>('email');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [canResend, setCanResend] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(60);
    const autoSendTriggeredRef = React.useRef(false);
    const autoSendOtpRef = React.useRef(autoSendOtp);
    const [lastSentAt, setLastSentAt] = useState<number | null>(null);

    // Keep ref in sync without changing dependency array sizes
    useEffect(() => {
        autoSendOtpRef.current = autoSendOtp;
    }, [autoSendOtp]);

    // Update email when initialEmail changes
    useEffect(() => {
        if (initialEmail) {
            setEmail(initialEmail);
        }
    }, [initialEmail]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setStep(autoSendOtpRef.current ? 'otp' : 'email');
            setOtp('');
            setError(null);
            if (initialEmail) setEmail(initialEmail);
            autoSendTriggeredRef.current = false;
        }
    }, [isOpen, initialEmail]);

    // Auto-send OTP when allowed (e.g., account already exists flow)
    useEffect(() => {
        if (!isOpen || !autoSendOtpRef.current || autoSendTriggeredRef.current) return;
        if (!isValidEmail(initialEmail || email)) return;

        autoSendTriggeredRef.current = true;
        const targetEmail = initialEmail || email;
        setEmail(targetEmail);
        setError(null);
        setIsSending(true);
        sendMagicLink(targetEmail)
            .then((sent) => {
                if (sent) setStep('otp');
            })
            .finally(() => setIsSending(false));
    }, [isOpen, initialEmail, email]);

    // Countdown for resend
    useEffect(() => {
        if (step === 'otp' && !canResend) {
            const timer = setInterval(() => {
                setResendCountdown((prev) => {
                    if (prev <= 1) {
                        setCanResend(true);
                        clearInterval(timer);
                        return 60;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [step, canResend]);

    const isValidEmail = (emailToCheck: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(emailToCheck) && emailToCheck.length >= 5;
    };

    const sendMagicLink = async (emailAddress: string) => {
        if (!isValidEmail(emailAddress)) {
            setError('Please enter a valid email address');
            return false;
        }

        // Guard against rapid re-requests; treat as success to avoid error surfacing
        if (lastSentAt && Date.now() - lastSentAt < 55000) {
            return true;
        }

        const result = await requestMagicLink({
            email: emailAddress,
            allowCreate: false,
            context: 'signin',
        });

        if (!result.ok) {
            if (result.code === 'rate_limited') {
                setLastSentAt(Date.now());
                return true;
            }
            setError(result.message);
            return false;
        }

        setStep('otp');
        setCanResend(false);
        setResendCountdown(60);
        setLastSentAt(Date.now());
        return true;
    };

    const handleSendMagicLink = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSending(true);
        await sendMagicLink(email);
        setIsSending(false);
    };

    const handleResend = async () => {
        setError(null);
        setIsSending(true); // Reuse isSending for loading state
        await sendMagicLink(email);
        setIsSending(false);
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!otp || otp.length < 6) return;

        setIsSending(true);
        setError(null);

        try {
            const supabase = createClient();
            const { data: { session }, error: verifyError } = await supabase.auth.verifyOtp({
                email,
                token: otp,
                type: 'email',
            });

            if (verifyError) throw verifyError;

            if (session?.user) {
                // Update store with auth details
                // Note: We do NOT load will data from DB here because we want to preserve
                // the current session's will data (supersede logic).
                // The willId in store should be preserved if it exists, or we'll let the checkout
                // process handle linking/creating the will.
                const currentWillId = useWillStore.getState().willId || '';
                setAuth(session.user.id, session.user.email || email, currentWillId);
                // Redirect to Review & Summary after sign-in
                useWillStore.getState().setStep(5);

                if (onSuccess) {
                    onSuccess();
                } else {
                    onClose();
                }
            }
        } catch (err: any) {
            console.error('Error verifying code:', err);
            setError(err.message || 'Invalid code. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <div className="space-y-5">
                {step === 'email' && !autoSendOtp ? (
                    <>
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
                                <Mail className="w-6 h-6 text-primary" />
                            </div>
                            <p className="text-muted-foreground text-sm">
                                Sign in to continue with your payment.
                            </p>
                        </div>

                        <form onSubmit={handleSendMagicLink} className="space-y-4">
                            <div>
                                <label htmlFor="signin-email" className="block text-sm font-medium text-foreground mb-2">
                                    Email address
                                </label>
                                <input
                                    type="email"
                                    id="signin-email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="name@example.com"
                                    className="w-full px-4 py-3 rounded-lg border border-foreground/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                    required
                                />
                            </div>

                            {error && (
                                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={isSending || !email}
                                className="w-full h-11"
                            >
                                {isSending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        Send sign in code
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </form>
                    </>
                ) : (
                    <>
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <h3 className="text-lg font-bold text-foreground mb-1">
                                Check your email
                            </h3>
                            <p className="text-muted-foreground text-sm mb-1">
                                We've sent a 6-digit code to
                            </p>
                            <p className="font-medium text-foreground text-sm">
                                {email}
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                                Enter the one-time passcode to continue with your payment.
                            </p>
                        </div>

                        <form onSubmit={handleVerifyOtp} className="space-y-4">
                            <div className="space-y-2">
                                <input
                                    type="text"
                                    placeholder="123456"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="w-full text-center text-2xl tracking-[0.5em] font-mono h-14 rounded-lg border border-foreground/20 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                    maxLength={6}
                                    required
                                    autoFocus
                                />
                            </div>

                            {error && (
                                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2 text-left">
                                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <Button
                                type="submit"
                                disabled={isSending || otp.length < 6}
                                className="w-full h-11"
                            >
                                {isSending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Verifying...
                                    </>
                                ) : (
                                    "Verify Code"
                                )}
                            </Button>
                        </form>

                        <div className="space-y-3 pt-2">
                            {canResend ? (
                                <Button variant="outline" onClick={handleResend} disabled={isSending} className="w-full h-10 text-sm">
                                    {isSending ? (
                                        <>
                                            <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        'Resend code'
                                    )}
                                </Button>
                            ) : (
                                <p className="text-xs text-center text-muted-foreground">
                                    Resend in {resendCountdown}s
                                </p>
                            )}
                            <button
                                onClick={() => {
                                    setStep('email');
                                    setOtp('');
                                    setError(null);
                                }}
                                className="w-full text-center text-sm text-primary hover:underline"
                            >
                                Use a different email
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};
